import { cloneElement, FC, forwardRef } from "react";
import { Pressable, PressableProps, StyleProp, StyleSheet, View, ViewProps, ViewStyle } from "react-native";

import { tPressableState, tSvgProps } from "../types";
import {
  ButtonContext,
  tPdButtonColorScheme,
  tPdButtonSize,
  tPdButtonVariant,
  useButtonLabelStyles,
  useIconButtonStyles,
} from "./hooks";

export type tPdBaseIconButton = {
  isDisabled?: boolean;
  icon: React.ReactElement<tSvgProps>;
  pressed: boolean;
  hovered?: boolean;
  focused?: boolean;
  contentContainerStyle?: StyleProp<ViewStyle>;
} & ViewProps;

export type tPdIconButtonProps = {
  isDisabled?: boolean;
  icon: React.ReactElement<tSvgProps>;
  colorScheme?: tPdButtonColorScheme;
  size?: tPdButtonSize;
  variant?: tPdButtonVariant;
  contentContainerStyle?: StyleProp<ViewStyle>;
} & PressableProps &
  ViewProps;

const BaseButton: FC<tPdBaseIconButton> = ({
  isDisabled,
  icon,
  pressed,
  hovered,
  focused,
  children,
  contentContainerStyle,
  ...rest
}) => {
  const buttonStyles = useIconButtonStyles(isDisabled);
  const buttonLabelStyles = useButtonLabelStyles();

  return (
    <View style={[styles.pdIconButton, buttonStyles, contentContainerStyle]} {...rest}>
      {cloneElement(icon, { color: buttonLabelStyles.color, style: icon.props.style })}
    </View>
  );
};

export const PdIconButton = forwardRef<View, React.PropsWithChildren<tPdIconButtonProps>>((props, ref) => {
  const { isDisabled, icon, colorScheme = "green", size = "md", variant = "solid", onPress, style, ...rest } = props;

  return (
    <Pressable disabled={isDisabled} onPress={onPress} style={style} ref={ref}>
      {({ pressed, hovered, focused }: tPressableState) => (
        <ButtonContext.Provider value={{ colorScheme, size, variant, pressed, hovered, focused }}>
          <BaseButton {...{ isDisabled, icon, pressed, hovered, focused }} {...rest} />
        </ButtonContext.Provider>
      )}
    </Pressable>
  );
});

const styles = StyleSheet.create({
  pdIconButton: {
    alignItems: "center",
    borderWidth: 2,
    justifyContent: "center",
  },
});
